import { loadMemoryState, saveMemoryState } from './storage';

export interface MemoryState {
  [key: string]: any;
}

export interface SetMemoryParams {
  userId: string;
  key: string;
  value: string;
}

export interface GetMemoryParams {
  userId: string;
  key: string;
}

// Cache para mantener el estado en memoria durante la sesión
const memoryCache: { [userId: string]: MemoryState } = {};

// Función para obtener el estado actual de un usuario
async function getUserState(userId: string): Promise<MemoryState> {
  if (!memoryCache[userId]) {
    memoryCache[userId] = await loadMemoryState(userId);
  }
  return { ...memoryCache[userId] };
}

// Función para actualizar el estado de un usuario
async function updateUserState(userId: string, key: string, value: any): Promise<MemoryState> {
  const currentState = await getUserState(userId);
  const newState = {
    ...currentState,
    [key]: value,
    lastInteraction: new Date().toISOString()
  };
  memoryCache[userId] = newState;
  saveMemoryState(userId, newState);
  return newState;
}

// Función para generar las instrucciones del sistema con el contexto de memoria
export function getMemorySystemInstructions(baseInstructions: string, userId?: string, state?: MemoryState): string {
  if (!userId || !state) return baseInstructions;

  return `${baseInstructions}

Current user context:
- User ID: ${userId}
- Memory state: ${JSON.stringify(state, null, 2)}

You can use the following memory tools:
- set_memory: Save or update information for the current user
- get_memory: Retrieve information for the current user

Always use the current user ID (${userId}) when using memory tools.`;
}

// Definición de las herramientas de memoria
export const memoryTools = [
  {
    schema: {
      name: 'set_memory',
      description: 'Saves or updates information in memory for a specific user',
      parameters: {
        type: 'object',
        properties: {
          userId: {
            type: 'string',
            description: 'The ID of the user whose memory to update (e.g., user1, user2, user3)',
          },
          key: {
            type: 'string',
            description: 'The key to store in memory (use lowercase with underscores)',
          },
          value: {
            type: 'string',
            description: 'The value to store',
          },
        },
        required: ['userId', 'key', 'value'],
      },
    },
    fn: async (params: SetMemoryParams) => {
      const newState = await updateUserState(params.userId, params.key, params.value);
      // Emitir un evento personalizado para notificar cambios en la memoria
      window.dispatchEvent(new CustomEvent('memory_updated', { 
        detail: { userId: params.userId, state: newState }
      }));
      return `Stored ${params.key}: ${params.value} for user ${params.userId}`;
    }
  },
  {
    schema: {
      name: 'get_memory',
      description: 'Retrieves information from memory for a specific user',
      parameters: {
        type: 'object',
        properties: {
          userId: {
            type: 'string',
            description: 'The ID of the user whose memory to query (e.g., user1, user2, user3)',
          },
          key: {
            type: 'string',
            description: 'The key to retrieve from memory',
          },
        },
        required: ['userId', 'key'],
      },
    },
    fn: async (params: GetMemoryParams) => {
      const state = await getUserState(params.userId);
      const value = state[params.key];
      return value !== undefined 
        ? `Value for ${params.key}: ${value}`
        : `No value found for key: ${params.key}`;
    }
  }
];

// Función para crear las herramientas de memoria
export function createMemoryTools() {
  return memoryTools;
}
